import { useState, useRef, useEffect, useCallback } from 'react';
import { computeInitialTransform, applyTransformToCanvas, snapRotation } from '@/lib/floorplanTransform';
import type { FloorplanTransform, AspectRatio } from '@/types';

interface UseFloorplanTransformArgs {
  imageUrl: string | null;
  frameRatio: AspectRatio; // '1:1' | '4:3'
  initialTransform: FloorplanTransform | null;
  onChange?: (t: FloorplanTransform) => void;
  /**
   * Optional callback fired after the floorplan base layer has been drawn.
   * Use it to draw additional layers (overlay, watermark, logo) on top of
   * the canvas. Wrap in `useCallback` with stable deps — every identity
   * change triggers a redraw of the canvas.
   */
  postRender?: (
    ctx: CanvasRenderingContext2D,
    frame: { width: number; height: number }
  ) => Promise<void> | void;
}

const FRAME_SIZE: Record<AspectRatio, { width: number; height: number }> = {
  '1:1': { width: 2048, height: 2048 },
  '4:3': { width: 2048, height: 1536 },
};

const DEFAULT_TRANSFORM: FloorplanTransform = { rotation: 0, scale: 1, tx: 0.5, ty: 0.5 };

const SCALE_MIN = 0.05;
const SCALE_MAX = 10;

const clamp = (v: number, lo: number, hi: number) => Math.max(lo, Math.min(hi, v));

const SNAP_TOL = 5;
const SNAP_ANGLES = [0, 90, 180, 270, 360];
export function snapRightAngle(deg: number): number {
  const norm = ((deg % 360) + 360) % 360;
  for (const s of SNAP_ANGLES) {
    if (Math.abs(norm - s) <= SNAP_TOL) return s === 360 ? 0 : s;
  }
  return norm;
}

export function useFloorplanTransform({
  imageUrl,
  frameRatio,
  initialTransform,
  onChange,
  postRender,
}: UseFloorplanTransformArgs) {
  const canvasRef = useRef<HTMLCanvasElement | null>(null);
  const imageRef = useRef<HTMLImageElement | null>(null);
  const [transform, setTransform] = useState<FloorplanTransform>(
    initialTransform ?? DEFAULT_TRANSFORM
  );
  const [isReady, setIsReady] = useState(false);
  const [imageNaturalSize, setImageNaturalSize] = useState<{ width: number; height: number } | null>(null);

  // Drag state — tracks pointer start + transform start for pointermove/up computation
  const dragStateRef = useRef<
    | {
        pointerId: number;
        startX: number;
        startY: number;
        startTx: number;
        startTy: number;
      }
    | null
  >(null);

  const frame = FRAME_SIZE[frameRatio];

  // Load image and run Auto-Init if no initialTransform
  useEffect(() => {
    if (!imageUrl) return;
    const img = new Image();
    img.crossOrigin = 'anonymous';
    img.onload = () => {
      imageRef.current = img;
      if (!initialTransform) {
        const t = computeInitialTransform(
          { width: img.naturalWidth, height: img.naturalHeight },
          frame
        );
        setTransform(t);
        onChange?.(t);
      }
      setImageNaturalSize({ width: img.naturalWidth, height: img.naturalHeight });
      setIsReady(true);
    };
    img.onerror = (e) => {
      console.error('[useFloorplanTransform] Image load failed:', e);
      setIsReady(false);
    };
    img.src = imageUrl;
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [imageUrl, frameRatio]);

  // Redraw on transform change
  useEffect(() => {
    const canvas = canvasRef.current;
    const img = imageRef.current;
    if (!canvas || !img || !isReady) return;

    canvas.width = frame.width;
    canvas.height = frame.height;
    const ctx = canvas.getContext('2d');
    if (!ctx) return;

    applyTransformToCanvas(ctx, img, transform, frame);
    if (postRender) {
      Promise.resolve(postRender(ctx, frame)).catch((e) =>
        console.warn('[useFloorplanTransform] postRender failed:', e)
      );
    }
  }, [transform, isReady, frame.width, frame.height, postRender]);

  const update = useCallback(
    (patch: Partial<FloorplanTransform>) => {
      setTransform((prev) => {
        const next = { ...prev, ...patch };
        onChange?.(next);
        return next;
      });
    },
    [onChange]
  );

  // Fit: preserves user's current rotation, only rescales to contain within frame
  const fit = useCallback(() => {
    const img = imageRef.current;
    if (!img) return;
    setTransform((prev) => {
      // Effective dims after current rotation (0°/180° keep, 90°/270° swap)
      const rotMod = ((prev.rotation % 180) + 180) % 180;
      const rotated = rotMod >= 45 && rotMod < 135;
      const effW = rotated ? img.naturalHeight : img.naturalWidth;
      const effH = rotated ? img.naturalWidth : img.naturalHeight;
      const scale = Math.min(frame.width / effW, frame.height / effH) * 0.95;
      const next = { ...prev, scale, tx: 0.5, ty: 0.5 };
      onChange?.(next);
      return next;
    });
  }, [frame, onChange]);

  // Reset: re-runs Auto-Init (may rotate 90° if orientation mismatches)
  const reset = useCallback(() => {
    const img = imageRef.current;
    if (!img) return;
    const t = computeInitialTransform(
      { width: img.naturalWidth, height: img.naturalHeight },
      frame
    );
    setTransform(t);
    onChange?.(t);
  }, [frame, onChange]);

  // Pointer drag: screen-space delta → frame-normalized delta applied to tx/ty.
  const onPointerDown = useCallback(
    (e: React.PointerEvent<HTMLCanvasElement>) => {
      const canvas = canvasRef.current;
      if (!canvas) return;
      // Capture the pointer so move/up continue to fire even outside canvas bounds.
      try {
        e.currentTarget.setPointerCapture(e.pointerId);
      } catch {
        // ignore — some browsers may throw if unsupported
      }

      dragStateRef.current = {
        pointerId: e.pointerId,
        startX: e.clientX,
        startY: e.clientY,
        startTx: transform.tx,
        startTy: transform.ty,
      };

      const handleMove = (ev: PointerEvent) => {
        const state = dragStateRef.current;
        if (!state || ev.pointerId !== state.pointerId) return;
        const rect = canvas.getBoundingClientRect();
        if (rect.width === 0 || rect.height === 0) return;
        const deltaX = (ev.clientX - state.startX) / rect.width;
        const deltaY = (ev.clientY - state.startY) / rect.height;
        const nextTx = clamp(state.startTx + deltaX, 0, 1);
        const nextTy = clamp(state.startTy + deltaY, 0, 1);
        setTransform((prev) => ({ ...prev, tx: nextTx, ty: nextTy }));
      };

      const handleUp = (ev: PointerEvent) => {
        const state = dragStateRef.current;
        if (!state || ev.pointerId !== state.pointerId) return;
        window.removeEventListener('pointermove', handleMove);
        window.removeEventListener('pointerup', handleUp);
        window.removeEventListener('pointercancel', handleUp);
        dragStateRef.current = null;
        // Flush final transform via onChange (setTransform callback gives us the latest)
        setTransform((prev) => {
          onChange?.(prev);
          return prev;
        });
      };

      window.addEventListener('pointermove', handleMove);
      window.addEventListener('pointerup', handleUp);
      window.addEventListener('pointercancel', handleUp);
    },
    [transform.tx, transform.ty, onChange]
  );

  // Corner-handle uniform scale (aspect-ratio-preserving).
  // Uses distance-from-center ratio: rotation-invariant, no sign-flip bugs.
  // scaleFactor = hypot(pointer - imageCenter) / hypot(startPointer - imageCenter)
  const applyCornerScale = useCallback(
    ({ scaleFactor, startScale }: { scaleFactor: number; startScale: number }) => {
      const nextScale = clamp(startScale * scaleFactor, SCALE_MIN, SCALE_MAX);
      setTransform((prev) => {
        const next = { ...prev, scale: nextScale };
        onChange?.(next);
        return next;
      });
    },
    [onChange]
  );

  // Absolute rotation setter, normalized to [0, 360) with snap at 0/90/180/270.
  const applyRotation = useCallback(
    ({ angleDeg }: { angleDeg: number }) => {
      const snapped = snapRightAngle(angleDeg);
      setTransform((prev) => {
        const next = { ...prev, rotation: snapped };
        onChange?.(next);
        return next;
      });
    },
    [onChange]
  );

  // Wheel zoom: deltaY<0 → zoom in, deltaY>0 → zoom out. Clamp scale to [0.05, 10].
  const onWheel = useCallback(
    (e: React.WheelEvent<HTMLCanvasElement>) => {
      e.preventDefault();
      const factor = e.deltaY < 0 ? 1.05 : 1 / 1.05;
      setTransform((prev) => {
        const nextScale = clamp(prev.scale * factor, SCALE_MIN, SCALE_MAX);
        const next = { ...prev, scale: nextScale };
        onChange?.(next);
        return next;
      });
    },
    [onChange]
  );

  return {
    canvasRef,
    transform,
    isReady,
    imageNaturalSize,
    frame,
    update,
    fit,
    reset,
    snapRotation,
    applyCornerScale,
    applyRotation,
    handlers: {
      onPointerDown,
      onWheel,
    },
  };
}
